import { create } from 'zustand';
import {
  startOfWeek, endOfWeek,
  startOfMonth, endOfMonth,
  startOfYear, endOfYear,
  addDays, subWeeks, addWeeks,
  parseISO,
} from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ViewMode = 'week-daily' | '2weeks-daily' | 'month-daily' | 'year-monthly' | 'year-daily';

export interface ScheduleItem {
  id: string;
  mondayItemId: number;
  name: string;
  assigneeId: number;
  startDate: string; // ISO date
  endDate: string;   // ISO date
  hours: number;
  /** Raw per-day hours from the scheduledHoursPerDayColumnId column (0 if not set) */
  hoursPerDay: number;
  /** Raw total hours from the scheduledHoursColumnId column (0 if not set) */
  hoursTotal: number;
  /** Per-item work hour format, resolved from which column has a value */
  workHourFormat: 'per_day' | 'total';
  /** True when both per-day and total columns have values > 0 */
  hasHoursConflict: boolean;
  color: string;
  groupId?: string;
  groupName?: string;
  groupColor?: string;
  status?: string;
  /** Hex color of the status label from monday.com column settings */
  statusColor?: string;
  /** Whether the item's status is marked as "done" in monday.com */
  isDone?: boolean;
  /** When the item is assigned to a team (not a person), this holds the team ID */
  assignedTeamId?: number;
  /** Board ID the item belongs to (for multi-board workspace filtering) */
  boardId?: number;
  /** Board name (populated during conversion for display in tooltips) */
  boardName?: string;
  /** True if this schedule item represents a subitem */
  isSubitem?: boolean;
  /** Parent item's monday.com ID (only set on subitems) */
  parentItemId?: number;
  /** Parent item's name (only set on subitems) */
  parentItemName?: string;
  /** The actual subitems board ID on monday.com (for write-back operations on subitems) */
  subitemsBoardId?: number;
  /** Original resolved hours before per-person split */
  originalTotalHours?: number;
  /** Number of assignees the hours are split across */
  assigneeCount?: number;
}

export interface DateRange {
  start: string; // ISO date
  end: string;   // ISO date
}

// ─── Persistence helpers ──────────────────────────────────────────────────────

const STORAGE_KEY = 'rp_schedule_view_state';

interface PersistedViewState {
  viewMode: ViewMode;
  anchorDate: string;
  hideWeekends?: boolean;
  firstDayOfWeek?: 0 | 1;
}

function calculateRange(anchor: Date, mode: ViewMode, weekStartsOn: 0 | 1 = 1): DateRange {
  switch (mode) {
    case 'week-daily':
      return { start: startOfWeek(anchor, { weekStartsOn }).toISOString(), end: endOfWeek(anchor, { weekStartsOn }).toISOString() };
    case '2weeks-daily': {
      const weekStart = startOfWeek(anchor, { weekStartsOn });
      return { start: weekStart.toISOString(), end: addDays(weekStart, 13).toISOString() };
    }
    case 'month-daily':
      return { start: startOfMonth(anchor).toISOString(), end: endOfMonth(anchor).toISOString() };
    case 'year-monthly':
    case 'year-daily':
      return { start: startOfYear(anchor).toISOString(), end: endOfYear(anchor).toISOString() };
  }
}

/**
 * Calculate a wider fetch range that includes a buffer around the visible range.
 * This avoids refetching when the user navigates forward/backward by one step.
 *
 * - week-daily:   -2 weeks / +3 weeks (6 weeks total) — favours forward navigation
 * - 2weeks-daily: -2 weeks / +2 weeks (6 weeks total)
 * - month/year:   no buffer (already wide enough)
 */
export function calculateFetchRange(visibleRange: DateRange, mode: ViewMode): DateRange {
  switch (mode) {
    case 'week-daily': {
      const start = subWeeks(parseISO(visibleRange.start), 2);
      const end = addWeeks(parseISO(visibleRange.end), 3);
      return { start: start.toISOString(), end: end.toISOString() };
    }
    case '2weeks-daily': {
      const start = subWeeks(parseISO(visibleRange.start), 2);
      const end = addWeeks(parseISO(visibleRange.end), 2);
      return { start: start.toISOString(), end: end.toISOString() };
    }
    default:
      // month-daily, year-monthly, year-daily — no buffer needed
      return visibleRange;
  }
}

/**
 * Check whether the visible range is fully contained within the previously fetched range.
 */
export function isRangeContained(visibleRange: DateRange, fetchedRange: DateRange | null): boolean {
  if (!fetchedRange) return false;
  return visibleRange.start >= fetchedRange.start && visibleRange.end <= fetchedRange.end;
}

const VALID_VIEW_MODES: ViewMode[] = ['week-daily', '2weeks-daily', 'month-daily', 'year-monthly', 'year-daily'];

function isValidViewMode(value: string): value is ViewMode {
  return VALID_VIEW_MODES.includes(value as ViewMode);
}

function loadPersistedState(): { viewMode: ViewMode; dateRange: DateRange; hideWeekends: boolean; firstDayOfWeek: 0 | 1 } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedViewState;
      if (parsed.viewMode && parsed.anchorDate && isValidViewMode(parsed.viewMode)) {
        const fdow: 0 | 1 = parsed.firstDayOfWeek ?? 1;
        return {
          viewMode: parsed.viewMode,
          dateRange: calculateRange(parseISO(parsed.anchorDate), parsed.viewMode, fdow),
          hideWeekends: parsed.hideWeekends ?? false,
          firstDayOfWeek: fdow,
        };
      }
    }
  } catch { /* ignore */ }
  return { viewMode: 'week-daily', dateRange: calculateRange(new Date(), 'week-daily'), hideWeekends: false, firstDayOfWeek: 1 };
}

function persistState(viewMode: ViewMode, dateRange: DateRange, hideWeekends: boolean, firstDayOfWeek?: 0 | 1) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ viewMode, anchorDate: dateRange.start, hideWeekends, firstDayOfWeek } as PersistedViewState));
  } catch { /* ignore */ }
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface ScheduleState {
  /** Scheduled items currently displayed on the board */
  items: ScheduleItem[];
  /** Current view granularity */
  viewMode: ViewMode;
  /** Visible date window */
  dateRange: DateRange;
  /** The date range that was actually fetched (includes buffer). Null if never fetched. */
  fetchedRange: DateRange | null;
  /** Filtered user IDs (empty = show all) */
  selectedUsers: number[];
  /** Whether the schedule data is currently loading */
  isLoading: boolean;
  /** Error message if the last fetch failed */
  error: string | null;
  /** First day of the week from monday.com account settings (0=Sunday, 1=Monday) */
  firstDayOfWeek: 0 | 1;
  /** Whether to hide weekend columns (Sat/Sun) from the scheduler */
  hideWeekends: boolean;
  /** Board IDs whose last fetch failed (typically due to a stored field-mapping
   *  column id that no longer exists on monday). Populated by useSchedule's
   *  multi-board fetch; surfaced in the UI so the user can reconfigure. */
  brokenBoardIds: number[];

  // ─── Actions ──────────────────────────────────────────────────────────────

  setItems: (items: ScheduleItem[]) => void;
  addItem: (item: ScheduleItem) => void;
  updateItem: (id: string, updates: Partial<ScheduleItem>) => void;
  removeItem: (id: string) => void;
  setViewMode: (mode: ViewMode) => void;
  setDateRange: (range: DateRange) => void;
  setFetchedRange: (range: DateRange | null) => void;
  setSelectedUsers: (userIds: number[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setFirstDayOfWeek: (day: 0 | 1) => void;
  setHideWeekends: (hide: boolean) => void;
  setBrokenBoardIds: (ids: number[]) => void;
}

const initialState = loadPersistedState();

export const useScheduleStore = create<ScheduleState>((set, get) => ({
  items: [],
  viewMode: initialState.viewMode,
  dateRange: initialState.dateRange,
  fetchedRange: null,
  selectedUsers: [],
  isLoading: false,
  error: null,
  firstDayOfWeek: initialState.firstDayOfWeek,
  hideWeekends: initialState.hideWeekends,
  brokenBoardIds: [],

  setItems: (items) => set({ items }),
  setBrokenBoardIds: (brokenBoardIds) => set({ brokenBoardIds }),

  addItem: (item) =>
    set((state) => ({ items: [...state.items, item] })),

  updateItem: (id, updates) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? { ...item, ...updates } : item,
      ),
    })),

  removeItem: (id) =>
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    })),

  setViewMode: (viewMode) => {
    const { dateRange, firstDayOfWeek, hideWeekends } = get();
    const anchor = parseISO(dateRange.start);
    const newRange = calculateRange(anchor, viewMode, firstDayOfWeek);
    // Invalidate fetched range — view mode change requires a fresh fetch
    set({ viewMode, dateRange: newRange, fetchedRange: null });
    persistState(viewMode, newRange, hideWeekends, firstDayOfWeek);
  },

  setDateRange: (dateRange) => {
    const { viewMode, hideWeekends, firstDayOfWeek } = get();
    set({ dateRange });
    persistState(viewMode, dateRange, hideWeekends, firstDayOfWeek);
  },

  setFetchedRange: (fetchedRange) => set({ fetchedRange }),

  setSelectedUsers: (selectedUsers) => set({ selectedUsers }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setFirstDayOfWeek: (day) => {
    const { viewMode, dateRange, hideWeekends } = get();
    // Recalculate range with the new first day if in week view
    if (viewMode === 'week-daily' || viewMode === '2weeks-daily') {
      const anchor = parseISO(dateRange.start);
      const newRange = calculateRange(anchor, viewMode, day);
      set({ firstDayOfWeek: day, dateRange: newRange });
      persistState(viewMode, newRange, hideWeekends, day);
    } else {
      set({ firstDayOfWeek: day });
      persistState(viewMode, dateRange, hideWeekends, day);
    }
  },

  setHideWeekends: (hideWeekends) => {
    const { viewMode, dateRange, firstDayOfWeek } = get();
    set({ hideWeekends });
    persistState(viewMode, dateRange, hideWeekends, firstDayOfWeek);
  },
}));
